import type { ClientDto, SessionType } from '../../../shared/types';

const pad = (n: number) => String(n).padStart(2, '0');

export function euro(cents: number): string {
  return (cents / 100).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
}

export function euroInput(cents: number | null | undefined): string {
  return cents == null ? '' : (cents / 100).toString().replace('.', ',');
}

export function parseEuro(value: string): number | null {
  const v = value.trim().replace(/\./g, '').replace(',', '.');
  if (v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

export function hm(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h}h ${pad(m)}m` : `${m}m`;
}

export function elapsed(startIso: string, now: number): string {
  let s = Math.max(0, Math.floor((now - new Date(startIso).getTime()) / 1000));
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  s -= m * 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function fmtDay(day: string): string {
  const [y, m, d] = day.split('-');
  return `${d}/${m}/${y}`;
}

/** Chiave giorno locale (YYYY-MM-DD) di una data. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function localDay(iso: string): string {
  return dayKey(new Date(iso));
}

export function todayKey(): string {
  return dayKey(new Date());
}

export function fmtClock(ms: number): string {
  return new Date(ms).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

/** Prima lettera maiuscola (le date italiane escono tutte minuscole). */
export function cap(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** "Mer, 23 settembre" */
export function fmtLongDate(ms: number): string {
  const d = new Date(ms);
  const wd = d.toLocaleDateString('it-IT', { weekday: 'short' }).replace('.', '');
  const rest = d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' });
  return `${cap(wd)}, ${rest}`;
}

/** "Martedì 23 settembre" */
export function dayLabel(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return cap(new Date(y, m - 1, d).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }));
}

export function thisMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

export function monthOf(day: string): string {
  return day.slice(0, 7);
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

/** "Settembre 2026" */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return cap(new Date(y, m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }));
}

export function typeLabel(type: SessionType): string {
  return type === 'call' ? 'Telefonata' : 'Lavoro';
}

/** Minuti dalla mezzanotte -> "20:00" (per <input type="time">). */
export function minuteToTime(m: number | null | undefined): string {
  if (m == null) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(Math.floor(m / 60))}:${p(m % 60)}`;
}

/** "20:00" -> minuti dalla mezzanotte. */
export function timeToMinute(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

export function nightLabel(c: ClientDto): string {
  return c.nightFromMinute == null || c.nightToMinute == null
    ? ''
    : `${minuteToTime(c.nightFromMinute)}-${minuteToTime(c.nightToMinute)}`;
}

export function rateLabel(c: ClientDto): string {
  const base = `${euro(c.hourlyRateCents)}/h`;
  if (c.extraHourlyRateCents == null) return base;
  const threshold = c.dailyThresholdMinutes != null ? ` oltre ${c.dailyThresholdMinutes / 60}h/giorno` : '';
  const night = nightLabel(c) ? `, di notte (${nightLabel(c)})` : '';
  return `${base} · ${euro(c.extraHourlyRateCents)}/h${threshold}${night} e nei giorni non lavorativi`;
}

export function rateShort(c: ClientDto): string {
  const base = `${euro(c.hourlyRateCents)}/h`;
  return c.extraHourlyRateCents == null ? base : `${base} · extra ${euro(c.extraHourlyRateCents)}/h`;
}

export function initials(name: string): string {
  // solo parole che iniziano con lettera o numero: niente parentesi o simboli, es. "(test)"
  const parts = name.trim().split(/\s+/).filter((p) => /^[\p{L}\p{N}]/u.test(p));
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const WEEKDAY_LABELS: Record<string, string> = {
  mon: 'Lun',
  tue: 'Mar',
  wed: 'Mer',
  thu: 'Gio',
  fri: 'Ven',
  sat: 'Sab',
  sun: 'Dom',
};

/** Valore per <input type="datetime-local"> (ora locale, senza secondi). */
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Dall'input data-ora all'istante assoluto (ISO) da mandare al server. */
export function fromLocalInput(value: string): string {
  return new Date(value).toISOString();
}

/** Sposta il valore dell'input di alcuni minuti (i pulsanti -5 / -15). */
export function shiftLocalInput(value: string, minutes: number): string {
  const d = new Date(value);
  d.setMinutes(d.getMinutes() + minutes);
  return toLocalInput(d.toISOString());
}
